import React from 'react';
import { useNavigate } from 'react-router-dom';
import ToolIcon from '../../../../../shared/ToolIcon/ToolIcon';
import StatusBadge from '../../../../../shared/StatusBadge/StatusBadge';
import { formatDate } from '../../../../../../utils/dateUtils';

/**
 * JSDoc for AnalysisDashboard component.
 * 
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.project - The current project object.
 * @param {Object} props.data - Analysis overview data from backend.
 * @param {Function} props.setActiveSubView - Callback to switch subviews ('timesheets'|'assignments'|'journal').
 * @param {Function} props.onTabChange - Callback to change modal main tab (e.g. 'team').
 * @returns {React.ReactElement} The dashboard grid layout.
 */
export default function AnalysisDashboard({ project, data, setActiveSubView, onTabChange }) {
    const navigate = useNavigate();
    const { timesheets, leader_assignments, team_summary, journal } = data;

    const formatHours = (h) => {
        if (!h) return '0.00';
        return parseFloat(h).toFixed(2);
    };

    return (
        <div className="project-analysis-tab">
            <div className="analysis-grid">

                {/* 1. STUNDENNACHWEIS CARD */}
                <div className="analysis-card glass-card">
                    <div className="analysis-card-header clickable" onClick={() => setActiveSubView('timesheets')}>
                        <div className="header-left">
                            <div className="icon-wrapper blue">
                                <ToolIcon name="timesheet" mode="svg" size={20} color="#ffffff" />
                            </div>
                            <h4>Stundennachweis</h4>
                        </div>
                        <span className="card-header-link">Details →</span>
                    </div>

                    <div className="analysis-stat">
                        <span className="stat-value">{formatHours(timesheets.total_hours)}</span>
                        <span className="stat-label">Stunden</span>
                    </div>

                    <div className="analysis-list">
                        <h5>Letzte Einträge:</h5>
                        {timesheets.recent?.length > 0 ? (
                            timesheets.recent.map(ts => (
                                <div
                                    key={ts.id}
                                    className="list-item clickable"
                                    onClick={() => {
                                        if (project) {
                                            sessionStorage.setItem('projectModalBackup', JSON.stringify({
                                                isOpen: true,
                                                activeTab: 'analysis',
                                                currentMode: 'view',
                                                project: project
                                            }));
                                            sessionStorage.setItem('projectAnalysis_activeSubView', 'dashboard');
                                        }
                                        navigate(`/admin/tools`, {
                                            state: {
                                                selectedToolKey: 'timesheet',
                                                focusUserId: ts.user_id,
                                                focusDate: ts.entry_date
                                            }
                                        });
                                    }}
                                >
                                    <div className="item-left">
                                        <span className="item-date">{formatDate(ts.entry_date)}</span>
                                        <span className="item-title">{ts.user_name}</span>
                                    </div>
                                    <span className="item-value">{formatHours(ts.hours)} Std.</span>
                                </div>
                            ))
                        ) : (
                            <p className="empty-text">Noch keine Stunden verbucht.</p>
                        )}

                        {timesheets.total_count > timesheets.recent?.length && (
                            <div className="more-indicator-wrapper" onClick={() => setActiveSubView('timesheets')}>
                                <span className="more-indicator">
                                    + {timesheets.total_count - timesheets.recent.length} weitere Einträge anzeigen
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. KOLONNENFÜHRER CARD */}
                <div className="analysis-card glass-card">
                    <div className="analysis-card-header clickable" onClick={() => setActiveSubView('assignments')}>
                        <div className="header-left">
                            <div className="icon-wrapper orange">
                                <ToolIcon name="leader" mode="svg" size={20} color="#ffffff" />
                            </div>
                            <h4>Kolonnenführer</h4>
                        </div>
                        <span className="card-header-link">Details →</span>
                    </div>

                    <div className="analysis-stat">
                        <span className="stat-value">{leader_assignments?.total_count || 0}</span>
                        <span className="stat-label">Geplante Einsätze</span>
                    </div>

                    <div className="analysis-list">
                        <h5>Aktuelle Einsätze:</h5>
                        {leader_assignments.recent?.length > 0 ? (
                            leader_assignments.recent.map(la => (
                                <div
                                    key={la.id}
                                    className="list-item clickable"
                                    onClick={() => {
                                        if (project) {
                                            sessionStorage.setItem('projectModalBackup', JSON.stringify({
                                                isOpen: true,
                                                activeTab: 'analysis',
                                                currentMode: 'view',
                                                project: project
                                            }));
                                            sessionStorage.setItem('projectAnalysis_activeSubView', 'dashboard');
                                        }
                                        navigate(`/tools/leader`, {
                                            state: {
                                                openAssignmentId: la.id
                                            }
                                        });
                                    }}
                                >
                                    <div className="item-left">
                                        <span className="item-date">
                                            {formatDate(la.hint_date_start)} {la.hint_date_end ? `- ${formatDate(la.hint_date_end)}` : ''}
                                        </span>
                                        <span className="item-title truncate">{la.title || 'Ohne Titel'}</span>
                                    </div>
                                    <StatusBadge status={la.status} />
                                </div>
                            ))
                        ) : (
                            <p className="empty-text">Keine Einsätze geplant.</p>
                        )}

                        {leader_assignments.total_count > leader_assignments.recent?.length && (
                            <div className="more-indicator-wrapper" onClick={() => setActiveSubView('assignments')}>
                                <span className="more-indicator">
                                    + {leader_assignments.total_count - leader_assignments.recent.length} weitere Einsätze anzeigen
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. TEAM ZUSAMMENFASSUNG CARD */}
                <div className="analysis-card glass-card clickable" onClick={() => onTabChange && onTabChange('team')}>
                    <div className="analysis-card-header">
                        <div className="header-left">
                            <div className="icon-wrapper green">
                                <ToolIcon name="team" mode="svg" size={20} color="#ffffff" />
                            </div>
                            <h4>Team & Ressourcen</h4>
                        </div>
                        <span className="card-header-link">Verwalten →</span>
                    </div>

                    <div className="analysis-stat">
                        <span className="stat-value">{(team_summary.employee_count || 0) + (team_summary.vehicle_count || 0)}</span>
                        <span className="stat-label">Ressourcen zugewiesen</span>
                    </div>

                    <div className="analysis-list">
                        <h5>Zusammensetzung:</h5>
                        <div className="list-item">
                            <span className="item-title">Mitarbeiter gesamt</span>
                            <span className="item-value">{team_summary.employee_count || 0}</span>
                        </div>
                        <div className="list-item">
                            <span className="item-title">Fahrzeuge</span>
                            <span className="item-value">{team_summary.vehicle_count || 0}</span>
                        </div>
                        <div className="list-item">
                            <span className="item-title">Kolonnen</span>
                            <span className="item-value">{team_summary.kolonnen_count || 0}</span>
                        </div>
                    </div>
                </div>

                {/* 4. BAUTAGEBUCH CARD */}
                <div className="analysis-card glass-card">
                    <div className="analysis-card-header clickable" onClick={() => setActiveSubView('journal')}>
                        <div className="header-left">
                            <div className="icon-wrapper purple">
                                <ToolIcon name="journal" mode="svg" size={20} color="#ffffff" />
                            </div>
                            <h4>Bautagebuch</h4>
                        </div>
                        <span className="card-header-link">Details →</span>
                    </div>

                    <div className="analysis-stat">
                        <span className="stat-value">{journal?.total_count || 0}</span>
                        <span className="stat-label">Neueste Berichte</span>
                    </div>

                    <div className="analysis-list">
                        <h5>Letzte Einträge:</h5>
                        {journal.recent?.length > 0 ? (
                            journal.recent.map(je => (
                                <div key={je.id} className="list-item clickable" onClick={() => {
                                    if (project) {
                                        sessionStorage.setItem('projectModalBackup', JSON.stringify({
                                            isOpen: true,
                                            activeTab: 'analysis',
                                            currentMode: 'view',
                                            project: project
                                        }));
                                        sessionStorage.setItem('projectAnalysis_activeSubView', 'dashboard');
                                    }
                                    navigate(`/tools/journal/${project.id}`, {
                                        state: {
                                            from: window.location.pathname,
                                            focusEntryId: je.id
                                        }
                                    });
                                }}>
                                    <div className="item-left">
                                        <span className="item-date">{formatDate(je.report_date)}</span>
                                        <span className="item-title truncate">{je.title || 'Tagesbericht'}</span>
                                    </div>
                                    <span className="item-value text-muted">{je.user_name}</span>
                                </div>
                            ))
                        ) : (
                            <p className="empty-text">Keine Tagebucheinträge vorhanden.</p>
                        )}

                        {journal.total_count > journal.recent?.length && (
                            <div className="more-indicator-wrapper" onClick={() => setActiveSubView('journal')}>
                                <span className="more-indicator">
                                    + {journal.total_count - journal.recent.length} weitere Berichte anzeigen
                                </span>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
